/**
 * Disability Service
 * API integration for Disabilities (Deficiências PCD) CRUD operations
 *
 * @version 2025.1.0
 * @description Serviço de integração com API de Deficiências PCD
 */
import apiClient from './apiClient';
import {
  Disability,
  DisabilityFilters,
  DisabilityCreateFormData,
  DisabilityUpdateFormData,
  DisabilityListResponse,
  DisabilityStatistics,
  MedicalCertificateInfo,
  DISABILITY_TYPE_OPTIONS,
  SEVERITY_LEVEL_OPTIONS,
} from '../types/disability';

// Re-export types for convenience
export * from '../types/disability';

export interface DisabilityListParams extends DisabilityFilters {
  page?: number;
  limit?: number;
}

class DisabilityService {
  private baseUrl = '/disabilities';

  /**
   * Get paginated list of disabilities
   */
  async getDisabilities(params: DisabilityListParams = {}): Promise<DisabilityListResponse> {
    const { page = 1, limit = 25, ...filters } = params;

    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (filters.search) queryParams.append('search', filters.search);
    if (filters.companyId) queryParams.append('companyId', filters.companyId.toString());
    if (filters.employeeName) queryParams.append('employeeName', filters.employeeName);
    if (filters.disabilityType) queryParams.append('disabilityType', filters.disabilityType);
    if (filters.severity) queryParams.append('severity', filters.severity);
    if (filters.isActive !== undefined) queryParams.append('isActive', filters.isActive.toString());
    if (filters.hasAccommodations !== undefined) queryParams.append('hasAccommodations', filters.hasAccommodations.toString());
    if (filters.sortBy) queryParams.append('sortBy', filters.sortBy);
    if (filters.sortOrder) queryParams.append('sortOrder', filters.sortOrder);

    const response = await apiClient.get(`${this.baseUrl}?${queryParams.toString()}`) as {
      message: string;
      data: Disability[];
      pagination?: { total: number; page: number; limit: number; totalPages: number };
    };

    // httpClient.get already returns response.data, so response = { message, data, pagination }
    return {
      disabilities: response.data ?? [],
      total: response.pagination?.total ?? 0,
      page: response.pagination?.page || page,
      limit: response.pagination?.limit || limit,
      totalPages: response.pagination?.totalPages || Math.ceil((response.pagination?.total ?? 0) / limit),
    };
  }

  /**
   * Get disability by ID
   */
  async getDisabilityById(id: string | number): Promise<Disability> {
    const response = await apiClient.get(`${this.baseUrl}/${id}`);
    return response.data.data || response.data;
  }

  /**
   * Create new disability
   */
  async createDisability(data: DisabilityCreateFormData): Promise<Disability> {
    const response = await apiClient.post(this.baseUrl, data);
    return response.data.data || response.data;
  }

  /**
   * Update disability by ID
   */
  async updateDisability(id: string | number, data: DisabilityUpdateFormData): Promise<Disability> {
    const response = await apiClient.put(`${this.baseUrl}/${id}`, data);
    return response.data.data || response.data;
  }

  /**
   * Delete disability by ID
   */
  async deleteDisability(id: string | number, hardDelete: boolean = false): Promise<void> {
    const queryParams = hardDelete ? '?hardDelete=true' : '';
    await apiClient.delete(`${this.baseUrl}/${id}${queryParams}`);
  }

  /**
   * Deactivate disability (soft delete)
   */
  async deactivateDisability(id: string | number): Promise<Disability> {
    const response = await apiClient.patch(`${this.baseUrl}/${id}/deactivate`);
    return response.data.data || response.data;
  }

  /**
   * Reactivate disability
   */
  async reactivateDisability(id: string | number): Promise<Disability> {
    const response = await apiClient.patch(`${this.baseUrl}/${id}/reactivate`);
    return response.data.data || response.data;
  }

  /**
   * Update accommodations
   */
  async updateAccommodations(id: string | number, accommodations: string): Promise<Disability> {
    const response = await apiClient.patch(`${this.baseUrl}/${id}/accommodations`, { accommodations });
    return response.data.data || response.data;
  }

  /**
   * Get disability statistics
   */
  async getStatistics(companyId?: number): Promise<DisabilityStatistics> {
    const queryParams = companyId ? `?companyId=${companyId}` : '';
    const response = await apiClient.get(`${this.baseUrl}/statistics${queryParams}`);
    return response.data.data || response.data;
  }

  /**
   * Get disabilities needing accommodations
   */
  async getNeedingAccommodations(companyId?: number): Promise<Disability[]> {
    const queryParams = companyId ? `?companyId=${companyId}` : '';
    const response = await apiClient.get(`${this.baseUrl}/needing-accommodations${queryParams}`);
    return response.data.data || response.data;
  }

  /**
   * Get disability types list
   */
  async getDisabilityTypes(): Promise<string[]> {
    try {
      const response = await apiClient.get(`${this.baseUrl}/types`);
      return response.data.data || DISABILITY_TYPE_OPTIONS;
    } catch {
      // Fallback to local constants if API fails
      return DISABILITY_TYPE_OPTIONS;
    }
  }

  /**
   * Get severity levels list
   */
  async getSeverityLevels(): Promise<string[]> {
    try {
      const response = await apiClient.get(`${this.baseUrl}/severity-levels`);
      return response.data.data || SEVERITY_LEVEL_OPTIONS;
    } catch {
      // Fallback to local constants if API fails
      return SEVERITY_LEVEL_OPTIONS;
    }
  }

  /**
   * Upload medical certificate (upsert - create or update)
   */
  async uploadMedicalCertificate(disabilityId: string | number, file: File): Promise<MedicalCertificateInfo> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post(
      `${this.baseUrl}/${disabilityId}/certificate`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    return response.data.data || response.data;
  }

  /**
   * Get medical certificate info
   */
  async getMedicalCertificate(disabilityId: string | number): Promise<MedicalCertificateInfo | null> {
    try {
      const response = await apiClient.get(`${this.baseUrl}/${disabilityId}/certificate`);
      return response.data.data || response.data;
    } catch (error: unknown) {
      if ((error as Record<string, unknown> & { response?: { status?: number } })?.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Get medical certificate download URL
   */
  getMedicalCertificateDownloadUrl(disabilityId: string | number): string {
    const baseUrl = import.meta.env.VITE_API_URL ?? '/api/v1';
    return `${baseUrl}${this.baseUrl}/${disabilityId}/certificate/download`;
  }

  /**
   * Delete medical certificate
   */
  async deleteMedicalCertificate(disabilityId: string | number): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${disabilityId}/certificate`);
  }

  /**
   * Export disabilities to Excel
   */
  async exportDisabilities(): Promise<Blob> {
    const response = await apiClient.get(`${this.baseUrl}/export`, { responseType: 'blob' });
    return response as unknown as Blob;
  }

  /**
   * Download import template
   */
  async downloadTemplate(): Promise<Blob> {
    const response = await apiClient.get(`${this.baseUrl}/template`, { responseType: 'blob' });
    return response as unknown as Blob;
  }

  /**
   * Import disabilities from Excel
   */
  async importDisabilities(file: File): Promise<{ imported: number; skipped: number; errors: Array<{ row: number; error: string }> }> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`${this.baseUrl}/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return (response as unknown as Record<string, unknown>).data as { imported: number; skipped: number; errors: Array<{ row: number; error: string }> } ?? response;
  }

  /**
   * Generate Laudo Caracterizador (deficiency report) PDF
   */
  async generateLaudoPdf(id: number): Promise<{ pdfPath: string }> {
    const response = await apiClient.get(`${this.baseUrl}/${id}/laudo/pdf`);
    return (response.data?.data ?? response.data) as { pdfPath: string };
  }

  /**
   * Get disability type options for select dropdowns
   */
  getDisabilityTypeOptions(): Array<{ value: string; label: string }> {
    return DISABILITY_TYPE_OPTIONS.map(type => ({ value: type, label: type }));
  }

  /**
   * Get severity level options for select dropdowns
   */
  getSeverityLevelOptions(): Array<{ value: string; label: string }> {
    return SEVERITY_LEVEL_OPTIONS.map(level => ({ value: level, label: level }));
  }

  /**
   * Health check
   */
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    const response = await apiClient.get(`${this.baseUrl}/health`);
    return response.data.data || response.data;
  }
}

// Export singleton instance
export const disabilityService = new DisabilityService();
export default disabilityService;
